import { Box, Card, CardContent, CardHeader, List, Typography } from '@mui/material';
import {
  FlagOutlined,
  DashboardOutlined,
  SettingsOutlined,
  CodeOutlined,
  SlideshowOutlined,
  WidgetsOutlined,
  HtmlOutlined,
  DnsOutlined,
  HubOutlined,
  Inventory2Outlined,
  AdminPanelSettingsOutlined,
  PaidOutlined,
  ExtensionOutlined,
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router';
import { useTranslations } from '@/i18n/use-translations';
import ActiveOrganizationMenuItem from '@/components/organizations/active-organization-menu/menu-item.tsx';
import type { ListElement } from '@/components/organizations/active-organization-menu/index.tsx';

export default function AdminMenu() {
  const t = useTranslations();
  const location = useLocation();

  const menuButtons: ListElement[] = [
    {
      text: t('features.title') || 'Features',
      icon: <FlagOutlined fontSize="inherit" />,
      type: 'features',
      path: '/features',
      count: undefined,
    },
    {
      text: t('admin.productCatalog') || 'Product catalog',
      icon: <Inventory2Outlined fontSize="inherit" />,
      type: 'productCatalog',
      path: '/admin/products',
      count: undefined,
    },
    {
      text: t('admin.subscriptionPlans') || 'Subscription plans',
      icon: <PaidOutlined fontSize="inherit" />,
      type: 'subscriptionPlans',
      path: '/admin/subscription-plans',
      count: undefined,
    },
    {
      text: t('admin.commercialFeatures') || 'Commercial features',
      icon: <ExtensionOutlined fontSize="inherit" />,
      type: 'commercialFeatures',
      path: '/admin/commercial-features',
      count: undefined,
    },
    {
      text: t('admin.entitlementsSupport') || 'Entitlements (support)',
      icon: <AdminPanelSettingsOutlined fontSize="inherit" />,
      type: 'entitlementsSupport',
      path: '/admin/entitlements',
      count: undefined,
    },
    {
      text: t('admin.swagger') || 'Swagger API Docs',
      icon: <CodeOutlined fontSize="inherit" />,
      type: 'swagger',
      path: '/swagger',
      count: undefined,
    },
    {
      text: t('admin.player.slideTemplates') || 'Slide Templates',
      icon: <SlideshowOutlined fontSize="inherit" />,
      type: 'slideTemplates',
      path: '/admin/player/slide-templates',
      count: undefined,
    },
    {
      text: t('admin.player.compositeComponents') || 'Composite Components',
      icon: <WidgetsOutlined fontSize="inherit" />,
      type: 'compositeComponents',
      path: '/admin/player/composite-components',
      count: undefined,
    },
    {
      text: t('admin.player.htmlComponents') || 'HTML Components',
      icon: <HtmlOutlined fontSize="inherit" />,
      type: 'htmlComponents',
      path: '/admin/player/html-components',
      count: undefined,
    },
    {
      text: t('admin.vmAdmin.title') || 'VM Management',
      icon: <DnsOutlined fontSize="inherit" />,
      type: 'vmAdmin',
      path: '/admin/vm',
      count: undefined,
    },
    {
      text: t('admin.opsAdmin.title') || 'Platform Ops',
      icon: <DashboardOutlined fontSize="inherit" />,
      type: 'opsAdmin',
      path: '/admin/ops',
      count: undefined,
    },
    {
      text: t('admin.agentPools.title') || 'Agent pools',
      icon: <HubOutlined fontSize="inherit" />,
      type: 'agentPools',
      path: '/admin/agents',
      count: undefined,
    },
  ];

  return (
    <Card
      sx={{
        width: 250,
        boxShadow: 'none',
        border: `1px solid`,
        borderColor: 'divider',
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
            <SettingsOutlined
              sx={{
                fontSize: '1.0rem',
                color: 'text.primary',
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  color: 'text.primary',
                  fontWeight: 500,
                }}
              >
                {t('admin.menu') || 'Admin'}
              </Typography>
            </Box>
          </Box>
        }
        sx={{
          'pl': 1,
          'pr': 2,
          'pt': 1.5,
          'pb': 1,
          'borderRadius': 0,
          '& .MuiCardHeader-content': {
            width: '100%',
            minWidth: 0,
          },
        }}
      />
      <CardContent
        sx={{
          'pt': 0.5,
          '&.MuiCardContent-root': {
            paddingBottom: '8px',
          },
          'pl': 2,
          'pr': 1,
          'bgcolor': 'background.paper',
          'textAlign': 'left',
        }}
      >
        <List sx={{ py: 0, ml: -1 }}>
          {menuButtons.map((listElement) => (
            <NavLink
              key={listElement.type}
              to={listElement.path || '#'}
              style={{
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <ActiveOrganizationMenuItem
                listElement={listElement}
                isSelected={
                  location.pathname === listElement.path || location.pathname.startsWith(listElement.path + '/')
                }
                onClick={() => {
                  // No-op, navigation handled by NavLink
                }}
              />
            </NavLink>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
